/**
 * OVATION auroral oval rendering, ported from loader-aurora.js.
 *
 * The pure part (grid build, bilinear sampling, colour ramp, pixel buffer) is
 * Cesium-free and canvas-free so it can be unit tested; renderAuroraDataUrl
 * draws the pixels to a canvas, applies the 2 px blur and returns a PNG data URL
 * that CesiumGlobe.setAuroraImage wraps in a SingleTileImageryProvider.
 *
 * Display rules: cells below 5% are transparent (OVATION's noise floor covers the
 * whole sphere at 1 to 3%); alpha rises quadratically from 0.20 at the threshold
 * to 0.85 at 100%; the colour ramp runs green, yellow, orange, red.
 */

export const AURORA_GRID_WIDTH = 360;
export const AURORA_GRID_HEIGHT = 180;
export const AURORA_CANVAS_WIDTH = 720;
export const AURORA_CANVAS_HEIGHT = 360;
export const AURORA_THRESHOLD_PCT = 5;
export const AURORA_BLUR_RADIUS_PX = 2;

export interface AuroraGrid {
  /** Dense 360 x 180 probability grid indexed [lat + 90][lon 0..359]. */
  grid: Float32Array;
  maxSeen: number;
  cells: number;
}

/**
 * Build the dense grid from OVATION records [lon, lat, prob] with lon 0..359
 * and lat -90..89. Longitudes outside 0..359 wrap; latitudes outside the grid are dropped.
 */
export function buildAuroraGrid(coordinates: ArrayLike<ArrayLike<number>>): AuroraGrid {
  const grid = new Float32Array(AURORA_GRID_WIDTH * AURORA_GRID_HEIGHT);
  let maxSeen = 0;
  let cells = 0;
  for (let i = 0; i < coordinates.length; i++) {
    const cell = coordinates[i];
    if (!cell || cell.length < 3) continue;
    let lon = cell[0];
    const lat = cell[1];
    const prob = cell[2];
    if (lon < 0) lon = ((lon % 360) + 360) % 360;
    if (lon > 359) lon = lon % 360;
    const latIdx = lat + 90;
    if (latIdx < 0 || latIdx > 179) continue;
    grid[latIdx * AURORA_GRID_WIDTH + Math.floor(lon)] = prob;
    if (prob > maxSeen) maxSeen = prob;
    cells++;
  }
  return { grid, maxSeen, cells };
}

/** Bilinear lookup at fractional grid coordinates; wraps in longitude and clamps in latitude. */
export function sampleBilinear(grid: Float32Array, latF: number, lonF: number): number {
  let lat0 = Math.floor(latF);
  let lat1 = lat0 + 1;
  const lon0 = ((Math.floor(lonF) % 360) + 360) % 360;
  const lon1 = (lon0 + 1) % 360;
  const dLat = latF - Math.floor(latF);
  const dLon = lonF - Math.floor(lonF);
  if (lat0 < 0) lat0 = 0;
  if (lat1 > 179) lat1 = 179;
  const v00 = grid[lat0 * AURORA_GRID_WIDTH + lon0];
  const v01 = grid[lat0 * AURORA_GRID_WIDTH + lon1];
  const v10 = grid[lat1 * AURORA_GRID_WIDTH + lon0];
  const v11 = grid[lat1 * AURORA_GRID_WIDTH + lon1];
  return (1 - dLat) * ((1 - dLon) * v00 + dLon * v01) + dLat * ((1 - dLon) * v10 + dLon * v11);
}

/** Probability percent (0..100) to RGBA; transparent below the threshold. */
export function probToRgba(prob: number): [number, number, number, number] {
  if (prob < AURORA_THRESHOLD_PCT) return [0, 0, 0, 0];
  const t = (prob - AURORA_THRESHOLD_PCT) / (100 - AURORA_THRESHOLD_PCT);
  const alpha = Math.min(0.85, t * t * 0.6 + 0.2);
  let r: number;
  let g: number;
  let b: number;
  if (prob < 15) {
    r = 58;
    g = 255;
    b = 140;
  } else if (prob < 30) {
    r = 124;
    g = 255;
    b = 58;
  } else if (prob < 50) {
    r = 255;
    g = 200;
    b = 58;
  } else if (prob < 70) {
    r = 255;
    g = 130;
    b = 50;
  } else {
    r = 255;
    g = 70;
    b = 70;
  }
  return [r, g, b, Math.round(alpha * 255)];
}

/**
 * Render the grid to an RGBA buffer (width x height x 4). Pixel x maps lon -180..180
 * left to right and pixel y maps lat 90..-90 top to bottom, both bilinearly sampled.
 */
export function renderAuroraPixels(
  grid: Float32Array,
  width: number = AURORA_CANVAS_WIDTH,
  height: number = AURORA_CANVAS_HEIGHT,
): Uint8ClampedArray {
  const data = new Uint8ClampedArray(width * height * 4);
  for (let y = 0; y < height; y++) {
    const lat = 90 - (y / (height - 1)) * 180;
    let latF = lat + 90;
    if (latF >= 179.999) latF = 179.999;
    if (latF < 0) latF = 0;
    for (let x = 0; x < width; x++) {
      const lon = -180 + (x / (width - 1)) * 360;
      const lonF = (lon + 360) % 360;
      const rgba = probToRgba(sampleBilinear(grid, latF, lonF));
      const idx = (y * width + x) * 4;
      data[idx] = rgba[0];
      data[idx + 1] = rgba[1];
      data[idx + 2] = rgba[2];
      data[idx + 3] = rgba[3];
    }
  }
  return data;
}

/**
 * Full pipeline: OVATION coordinates to a 720 x 360 PNG data URL with a 2 px blur.
 * Throws when there are no usable cells or no 2D canvas context (browser only).
 */
export function renderAuroraDataUrl(coordinates: ArrayLike<ArrayLike<number>>): string {
  const { grid, cells } = buildAuroraGrid(coordinates);
  if (cells === 0) throw new Error('Aurora: OVATION response had no usable coordinates');
  const pixels = renderAuroraPixels(grid);

  let canvas = document.createElement('canvas');
  canvas.width = AURORA_CANVAS_WIDTH;
  canvas.height = AURORA_CANVAS_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Aurora: 2D canvas context unavailable');
  const imgData = ctx.createImageData(AURORA_CANVAS_WIDTH, AURORA_CANVAS_HEIGHT);
  imgData.data.set(pixels);
  ctx.putImageData(imgData, 0, 0);

  if (AURORA_BLUR_RADIUS_PX > 0 && 'filter' in ctx) {
    const blurred = document.createElement('canvas');
    blurred.width = AURORA_CANVAS_WIDTH;
    blurred.height = AURORA_CANVAS_HEIGHT;
    const bctx = blurred.getContext('2d');
    if (bctx) {
      bctx.filter = `blur(${AURORA_BLUR_RADIUS_PX}px)`;
      bctx.drawImage(canvas, 0, 0);
      canvas = blurred;
    }
  }
  return canvas.toDataURL('image/png');
}
